// Full-screen editor modal for a product's "Learn More" content.
//
// Opens a TipTap rich-text editor with a live preview tab. Admins get a
// working area sized close to the actual "Learn More" dialog so they can
// judge layout while writing. On save, the serialized TipTap JSON string is
// passed back to the parent via onSave.
import { useEffect, useState, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  parseTiptapValue, tiptapToJsonString, renderableHtml,
} from '../../../utils/rtf-helpers.js';
import LearnMoreEditorToolbar from './LearnMoreEditorToolbar.jsx';

const LearnMoreEditorModal = ({
  open, value, onClose, onSave,
}) => {
  const [activeTab, setActiveTab] = useState(0);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
    ],
    content: parseTiptapValue(value),
  });

  // Sync editor content when the modal opens with a new value.
  useEffect(() => {
    if (open && editor) {
      const doc = parseTiptapValue(value);
      editor.commands.setContent(doc ?? { type: 'doc', content: [] });
    }
  }, [open, value, editor]);

  // Always reset to the edit tab when the modal opens.
  useEffect(() => {
    if (open) setActiveTab(0);
  }, [open]);

  const handleSave = useCallback(() => {
    if (!editor) return;
    onSave(tiptapToJsonString(editor.getJSON()));
    onClose();
  }, [editor, onSave, onClose]);

  // Live preview HTML from the editor's current state.
  const previewHtml = editor ? renderableHtml(tiptapToJsonString(editor.getJSON())) : '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="learn-more-editor-title"
    >
      <DialogTitle
        id="learn-more-editor-title"
        sx={{ pr: 6, pb: 0 }}
      >
        Edit &ldquo;Learn More&rdquo; content
      </DialogTitle>
      <IconButton
        aria-label="Close editor"
        onClick={onClose}
        sx={{
          position: 'absolute', top: 8, right: 8, color: 'text.secondary',
        }}
      >
        <CloseIcon />
      </IconButton>

      <Box sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={(_e, v) => setActiveTab(v)} aria-label="Editor tabs">
          <Tab label="Edit" id="editor-tab-edit" aria-controls="editor-panel-edit" />
          <Tab label="Preview" id="editor-tab-preview" aria-controls="editor-panel-preview" />
        </Tabs>
      </Box>

      <DialogContent sx={{
        p: 0, display: 'flex', flexDirection: 'column', minHeight: 420,
      }}
      >
        {/* Edit tab */}
        <Box
          role="tabpanel"
          id="editor-panel-edit"
          aria-labelledby="editor-tab-edit"
          hidden={activeTab !== 0}
          sx={{
            display: activeTab === 0 ? 'flex' : 'none',
            flexDirection: 'column',
            flex: 1,
          }}
        >
          {editor && (
            <>
              <LearnMoreEditorToolbar editor={editor} />
              <Box
                sx={{
                  flex: 1,
                  overflow: 'auto',
                  px: 3,
                  py: 2,
                  '& .tiptap': {
                    minHeight: 300,
                    outline: 'none',
                    fontSize: '0.9rem',
                    lineHeight: 1.6,
                    color: 'text.primary',
                    '& p': { mt: 0, mb: 1 },
                    '& h2': {
                      mt: 1.5, mb: 0.5, fontSize: '1.1rem', fontWeight: 700,
                    },
                    '& h3': {
                      mt: 1.5, mb: 0.5, fontSize: '1rem', fontWeight: 600,
                    },
                    '& ul, & ol': { pl: 3, mb: 1 },
                    '& li': { mb: 0.25 },
                    '& a': { color: 'primary.main' },
                    '& blockquote': {
                      borderLeft: '3px solid', borderColor: 'divider', pl: 2, ml: 0, color: 'text.secondary',
                    },
                    '& code': {
                      fontFamily: 'monospace',
                      fontSize: '0.85em',
                      bgcolor: 'action.hover',
                      px: 0.5,
                      borderRadius: 0.5,
                    },
                    '& pre': {
                      bgcolor: 'action.hover',
                      p: 1.5,
                      borderRadius: 1,
                      '& code': { bgcolor: 'transparent', px: 0 },
                    },
                    '& .ProseMirror-focused': { outline: 'none' },
                    // Placeholder rendered by ProseMirror when doc is empty.
                    '& p.is-editor-empty:first-of-type::before': {
                      content: 'attr(data-placeholder)',
                      color: 'text.disabled',
                      pointerEvents: 'none',
                      float: 'left',
                      height: 0,
                    },
                  },
                }}
              >
                <EditorContent editor={editor} placeholder="Write product details here…" />
              </Box>
            </>
          )}
        </Box>

        {/* Preview tab */}
        <Box
          role="tabpanel"
          id="editor-panel-preview"
          aria-labelledby="editor-tab-preview"
          hidden={activeTab !== 1}
          sx={{
            display: activeTab === 1 ? 'flex' : 'none',
            flexDirection: 'column',
            flex: 1,
            p: 3,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            This is how the content will appear in the public &ldquo;Learn More&rdquo; dialog.
          </Typography>
          {previewHtml ? (
            <Box
              sx={{
                fontSize: '0.9rem',
                lineHeight: 1.6,
                color: 'text.primary',
                '& p': { mt: 0, mb: 1 },
                '& h2': {
                  mt: 1.5, mb: 0.5, fontSize: '1.1rem', fontWeight: 700,
                },
                '& h3': {
                  mt: 1.5, mb: 0.5, fontSize: '1rem', fontWeight: 600,
                },
                '& ul, & ol': { pl: 3, mb: 1 },
                '& li': { mb: 0.25 },
                '& a': { color: 'primary.main' },
                '& blockquote': {
                  borderLeft: '3px solid', borderColor: 'divider', pl: 2, ml: 0, color: 'text.secondary',
                },
              }}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          ) : (
            <Typography variant="body2" color="text.disabled">
              Nothing to preview yet — add some content on the Edit tab.
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Tooltip title="Discard changes and close">
          <Button onClick={onClose} color="inherit">Cancel</Button>
        </Tooltip>
        <Button onClick={handleSave} variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LearnMoreEditorModal;
